(() => {
    const tagName = 'aura-blog-article';

    if (customElements.get(tagName)) return;

    class AuraBlogArticle extends HTMLElement {
      connectedCallback() {
        if (this.dataset.erReady === 'true') return;
        this.dataset.erReady = 'true';

        this.image = this.querySelector(`.er-aura-article__hero-image-${this.dataset.componentId}`);
        this.cache = window.__erAuraArticlePaletteCache || new Map();
        window.__erAuraArticlePaletteCache = this.cache;

        this.applyPalette();
      }

      clamp(value, min, max) {
        return Math.max(min, Math.min(max, value));
      }

      colorDistance(a, b) {
        return Math.hypot(
          a[0] - b[0],
          a[1] - b[1],
          a[2] - b[2]
        );
      }

      vividify(color, strength = 1.78) {
        const [r, g, b] = color;
        const luma = (.2126 * r) + (.7152 * g) + (.0722 * b);

        return [r, g, b].map((channel) => {
          const tuned = luma + ((channel - luma) * strength);
          return Math.round(
            this.clamp((tuned * 1.03) + 3, 10, 246)
          );
        });
      }

      extractPalette(image) {
        const canvas = document.createElement('canvas');
        const size = 44;
        canvas.width = size;
        canvas.height = size;

        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return null;

        context.drawImage(image, 0, 0, size, size);
        const pixels = context.getImageData(0, 0, size, size).data;
        const buckets = new Map();

        for (let index = 0; index < pixels.length; index += 4) {
          if (pixels[index + 3] < 150) continue;

          const r = pixels[index];
          const g = pixels[index + 1];
          const b = pixels[index + 2];
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          const luminance = (.2126 * r) + (.7152 * g) + (.0722 * b);

          if (luminance < 14 || luminance > 244) continue;

          const qr = Math.min(255, Math.round(r / 30) * 30);
          const qg = Math.min(255, Math.round(g / 30) * 30);
          const qb = Math.min(255, Math.round(b / 30) * 30);
          const key = `${qr},${qg},${qb}`;

          const bucket = buckets.get(key) || {
            score: 0,
            count: 0,
            r: 0,
            g: 0,
            b: 0
          };

          bucket.score += (.28 + (saturation * 4.25)) * (
            luminance > 28 && luminance < 230 ? 1.24 : .76
          );
          bucket.count += 1;
          bucket.r += r;
          bucket.g += g;
          bucket.b += b;
          buckets.set(key, bucket);
        }

        const ranked = [...buckets.values()]
          .filter((bucket) => bucket.count >= 2)
          .map((bucket) => ({
            score: bucket.score,
            color: [
              Math.round(bucket.r / bucket.count),
              Math.round(bucket.g / bucket.count),
              Math.round(bucket.b / bucket.count)
            ]
          }))
          .sort((a, b) => b.score - a.score);

        if (!ranked.length) return null;

        const primary = this.vividify(ranked[0].color, 1.88);
        let secondary = primary;

        for (const candidate of ranked.slice(1, 16)) {
          const tuned = this.vividify(candidate.color, 1.70);

          if (this.colorDistance(primary, tuned) > 64) {
            secondary = tuned;
            break;
          }
        }

        if (secondary === primary && ranked[1]) {
          secondary = this.vividify(ranked[1].color, 1.58);
        }

        const mid = primary.map(
          (channel, index) => Math.round((channel + secondary[index]) / 2)
        );

        const accent = primary.map(
          (channel) => Math.round(
            this.clamp((channel * 1.04) + 8, 14, 250)
          )
        );

        const softPrimary = primary.map(
          (channel) => Math.round(
            channel + ((255 - channel) * .34)
          )
        );

        const softSecondary = secondary.map(
          (channel) => Math.round(
            channel + ((255 - channel) * .28)
          )
        );

        return {
          primary,
          secondary,
          mid,
          accent,
          softPrimary,
          softSecondary
        };
      }

      getPalette(image) {
        const source = image.currentSrc || image.src || '';
        if (!source) return Promise.resolve(null);

        if (this.cache.has(source)) {
          return this.cache.get(source);
        }

        const promise = new Promise((resolve) => {
          if (image.complete && image.naturalWidth > 0) {
            try {
              const direct = this.extractPalette(image);

              if (direct) {
                resolve(direct);
                return;
              }
            } catch (error) {
              /* Retry using a CORS-enabled sampler below. */
            }
          }

          const sampler = new Image();
          sampler.crossOrigin = 'anonymous';
          sampler.decoding = 'async';

          sampler.onload = () => {
            try {
              resolve(this.extractPalette(sampler));
            } catch (error) {
              resolve(null);
            }
          };

          sampler.onerror = () => resolve(null);
          sampler.src = source;
        });

        this.cache.set(source, promise);
        return promise;
      }

      async applyPalette() {
        if (!(this.image instanceof HTMLImageElement)) return;

        const palette = await this.getPalette(this.image);
        if (!palette || !this.isConnected) return;

        this.style.setProperty(
          '--er-article-primary',
          palette.primary.join(', ')
        );
        this.style.setProperty(
          '--er-article-secondary',
          palette.secondary.join(', ')
        );
        this.style.setProperty(
          '--er-article-mid',
          palette.mid.join(', ')
        );
        this.style.setProperty(
          '--er-article-accent',
          palette.accent.join(', ')
        );
        this.style.setProperty(
          '--er-article-soft-primary',
          palette.softPrimary.join(', ')
        );
        this.style.setProperty(
          '--er-article-soft-secondary',
          palette.softSecondary.join(', ')
        );

        this.classList.add('has-aura-palette');
      }
    }

    customElements.define(tagName, AuraBlogArticle);
  })();
